import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Modal } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import RoleBadge from "~/app/settings/organization/components/RoleBadge";

import { cn } from "~/core/generic/shadcn-utils";
import { Avatar, AvatarFallback, AvatarImage } from "~/core/ui/Avatar";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/core/ui/Command";
import If from "~/core/ui/If";

import useCurrentOrganization from "~/lib/organizations/hooks/use-current-organization";
import useUserSession from "~/lib/organizations/hooks/use-user-session";

import { faCheck, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { useSwitchWorkspace } from "./hooks/useSwitchWorkspace";

type SwitchWorkspaceProps = {
  open: boolean;
  onClose: () => void;
};

export default function SwitchWorkspace({
  onClose,
  open,
}: SwitchWorkspaceProps) {
  const { userSession } = useUserSession();
  const currentOrg = useCurrentOrganization();
  const { switchWorkspace } = useSwitchWorkspace();

  const inputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const filteredOrganizations = useMemo(
    () =>
      userSession?.organizations?.filter(
        organization =>
          organization.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          organization.domain.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [searchTerm, userSession?.organizations],
  );

  return (
    <Modal
      centered
      footer={null}
      closeIcon={null}
      open={open}
      onCancel={onClose}
      className="[&>div>.ant-modal-content]:!p-0 [&>div>.ant-modal-content]:!shadow-lg"
    >
      <div className="p-3 flex flex-col items-center justify-center gap-3 pb-0">
        <div className="flex items-center justify-between w-full">
          <h3 className="text-lg font-semibold">Switch workspace</h3>
          <Button
            icon={<FontAwesomeIcon icon={faXmark} />}
            type="text"
            onClick={onClose}
          />
        </div>
      </div>
      <div className="w-full">
        <Command>
          <CommandInput
            ref={inputRef}
            className="p-0"
            onChangeCapture={handleSearch}
            placeholder="Search for workspace"
          />
          <CommandEmpty className="p-4 text-center text-muted-foreground">
            No worskpace found with the keyword
          </CommandEmpty>
          <CommandList>
            {filteredOrganizations?.map(organization => (
              <CommandItem
                className={cn(
                  "py-4 !px-3 flex items-center justify-between text-sm text-primary font-medium",
                )}
                key={organization.org_id}
                value={organization.name}
                onSelect={() => {
                  if (organization.org_id !== currentOrg?.org_id) {
                    switchWorkspace(organization.org_id);
                  } else {
                    onClose();
                  }
                }}
              >
                <div className="flex items-center gap-2">
                  <Avatar>
                    <AvatarFallback>
                      {organization.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                    <AvatarImage
                      src={organization.logo_url}
                      alt={organization.name}
                    />
                  </Avatar>
                  <div>
                    {organization.name}
                    <div className="flex items-center gap-2">
                      <p className="font-light">{organization.domain}</p>
                      <RoleBadge role={organization.role} />
                    </div>
                  </div>
                </div>
                <If condition={currentOrg?.org_id === organization.org_id}>
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="size-4 text-primary mr-2"
                  />
                </If>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </div>
    </Modal>
  );
}
